import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { Button, DatePicker, Drawer, Upload } from "antd";
import { priority, projectProgress } from "../../../core/common/selectoption/selectoption";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllClients } from "../../../core/data/redux/projects/client/clientAction";
import { AppDispatch } from "../../../core/data/redux/store";
import { createProject, deleteProject, fetchAllProjects, updateProject } from "../../../core/data/redux/projects/project/projectAction";
import dayjs from "dayjs";
import { formatDate } from "../../../core/utils/configFormatters";
import { message } from "antd";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import Select from "react-select";
import { getEmployees } from "../../../core/data/redux/employee/employeeAction";
import { createProjectMember, updateProjectMember } from "../../../core/data/redux/projects/projectmember/projectMemberAction";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import { status as projectStatus } from "../../../core/common/selectoption/selectoption";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { decryptData } from "../../../core/utils/encryptUtils";
import { UploadOutlined } from "@ant-design/icons";

const statusChoose = [
  { value: "Select", label: "Select" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

type EmployeeOption = {
  value: number | string;
  label: string;
};

type ProjectMemberFormData = {
  employee_ids: EmployeeOption[];
  team_lead_employee_id: string;
  project_manager_employee_id: string;
  projectmembers_status: string;
};

const ProjectList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { user } = useSelector((state: any) => state.auth);
  const { clients } = useSelector((state: any) => state.project.clients);
  const { employees } = useSelector((state: any) => state.employee);
  const employeeData = employees ? employees : [];
  const { projects, loading } = useSelector((state: any) => state.project.projects);
  const { projectsData } = useSelector((state: any) => state.project.projects);
  const clientActiveData = filterByStatus(clients, "active", "status");
  const clientData = useMemo(() => {
    return clientActiveData
      ? clientActiveData.filter((client: any) => client.client_or_vendor === 0)
      : [];
  }, [clientActiveData]);

  const [fileList, setFileList] = useState<any>([]);

  const [projectFormData, setProjectFormData] = useState({
    project_name: "",
    client_id: "",
    total_hours: "",
    priority: "",
    start_date: dayjs(),
    end_date: dayjs(),
    project_value: 0,
    price_type: "",
    description: "",
    project_doc: null,
    project_progress: "",
    status: "active",
  });
  const [projectMemberFormData, setProjectMemberFormData] =
    useState<ProjectMemberFormData>({
      employee_ids: [],
      team_lead_employee_id: "",
      project_manager_employee_id: "",
      projectmembers_status: "",
    });

  const [errors, setErrors] = useState({
    project_name: "",
    client_id: "",
    total_hours: "",
    priority: "",
    project_value: "",
    price_type: "",
    project_progress: "",
    start_date: "",
    status: "",
  });
  const [membersErrors, setMembersErrors] = useState({
    employee_ids: "",
    team_lead_employee_id: "",
    project_manager_employee_id: "",
    projectmembers_status: "",
  });

  const validateForm = () => {
    const newErrors: any = {};
    let isValid = true;

    const numberRegex = /^\d+(\.\d+)?$/;

    // Validate project_name
    if (!projectFormData.project_name) {
      newErrors.project_name = "Project name is required";
      isValid = false;
    }
    if (
      !projectFormData.total_hours ||
      !numberRegex.test(projectFormData.total_hours)
    ) {
      newErrors.total_hours = "Please enter a valid number (e.g., 10 or 20)";
      isValid = false;
    }

    // Validate client_id
    if (!projectFormData.client_id) {
      newErrors.client_id = "Client is required";
      isValid = false;
    }

    // Validate priority
    if (!projectFormData.priority) {
      newErrors.priority = "Priority is required";
      isValid = false;
    }

    // Validate project_value
    if (
      !projectFormData.project_value ||
      isNaN(projectFormData.project_value)
    ) {
      newErrors.project_value = "Project value must be a number";
      isValid = false;
    }

    // Validate price_type
    if (!projectFormData.price_type) {
      newErrors.price_type = "Currency type is required";
      isValid = false;
    }
    if (!projectFormData.project_progress) {
      newErrors.project_progress = "Project Progress is required";
      isValid = false;
    }
    if (projectFormData.start_date && projectFormData.end_date) {
      const start = dayjs(projectFormData.start_date);
      const end = dayjs(projectFormData.end_date);
      if (start > end) {
        // newErrors.start_date = 'Start date cannot be after end date';
        message.error("Start date cannot be after end date");
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const validateProjectMemberForm = () => {
    const newErrors: any = {};
    let isValid = true;

    if (!projectMemberFormData.team_lead_employee_id) {
      newErrors.team_lead_employee_id = "Team Leader is required";
      isValid = false;
    }
    if (!projectMemberFormData.project_manager_employee_id) {
      newErrors.project_manager_employee_id = "Project Manager is required";
      isValid = false;
    }

    if (projectMemberFormData.employee_ids.length === 0) {
      newErrors.employee_ids = "At least one team member must be selected";
      isValid = false;
    }

    if (!projectMemberFormData.projectmembers_status) {
      newErrors.projectmembers_status = "Status is required";
      isValid = false;
    }

    setMembersErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setProjectFormData({
      project_name: "",
      client_id: "",
      total_hours: "",
      priority: "",
      start_date: dayjs(),
      end_date: dayjs(),
      project_value: 0,
      price_type: "",
      description: "",
      project_doc: null,
      project_progress: "",
      status: "active",
    });
    setFileList([]);
    // Reset the validation errors as well
    setErrors({
      project_name: "",
      client_id: "",
      total_hours: "",
      priority: "",
      project_value: "",
      price_type: "",
      project_progress: "",
      start_date: "",
      status: "",
    });
  };

  const resetProjectMemberForm = () => {
    setProjectMemberFormData({
      employee_ids: [],
      team_lead_employee_id: "",
      project_manager_employee_id: "",
      projectmembers_status: "",
    });
    setMembersErrors({
      employee_ids: "",
      team_lead_employee_id: "",
      project_manager_employee_id: "",
      projectmembers_status: "",
    });
  };

  useEffect(() => {
    if (!projects.length) {
      dispatch(fetchAllProjects());
    }
    if (!clients.length) {
      dispatch(fetchAllClients());
    }
    if (!employees.length) dispatch(getEmployees());
  }, [dispatch]);

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body;
  };

  const showModal = (type: "edit" | "create") => {
    if (type === "create") {
      setSelectedRowData(null);
      resetForm();
      resetProjectMemberForm();
    }
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    setSelectedRowData(null);
    resetForm();
    resetProjectMemberForm();
  };

  const handleEditProject = (record: any): void => {
    setSelectedRowData(record);
    setProjectFormData({
      project_name: record.project_name,
      client_id: record.client_id,
      total_hours: record.total_hours,
      priority: record.priority,
      start_date: record.start_date ? dayjs(record.start_date) : dayjs(),
      end_date: record.end_date ? dayjs(record.end_date) : dayjs(),
      project_value: record.project_value,
      price_type: record.price_type,
      description: record.description,
      project_doc: record.project_doc_url,
      project_progress: record.project_progress,
      status: record.status,
    });
    if (record.project_doc_url) {
      const fileName = record.project_doc_url.split("/").pop();
      setFileList([
        {
          uid: "-1",
          name: fileName || "Uploaded File",
          status: "done",
          url: record.project_doc_url,
        },
      ]);
    } else {
      setFileList([]);
    }
    setProjectMemberFormData({
      employee_ids: record.employee_ids || [],
      team_lead_employee_id: record.team_lead_employee_id || "",
      project_manager_employee_id: record.project_manager_employee_id || "",
      projectmembers_status: record.projectmembers_status || "",
    });
    showModal("edit");
  };

  const handleInputChange = (e: any, field: string) => {
    const { value } = e.target;
    setProjectFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };
  const handleProjectMemberMultiChange = (
    field: string,
    selectedEmployees: any
  ) => {
    const updatedValue = selectedEmployees.map((emp: any) => emp.value);
    setProjectMemberFormData((prevState) => ({
      ...prevState,
      [field]: updatedValue,
    }));
  };
  const handleProjectMemberChange = (field: string, value: any) => {
    setProjectMemberFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleFileChange = (fileOrEvent: any, field: string) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent; // handle both event or direct file
    setProjectFormData((prevState) => ({
      ...prevState,
      [field]: file,
    }));
  };

  const props = {
    beforeUpload: (file: any) => {
      setFileList([file]);
      handleFileChange(file, "project_doc");
      return false; // prevent auto-upload
    },
    onRemove: () => {
      setFileList([]);
      setProjectFormData((prevState) => ({
        ...prevState,
        project_doc: null,
      }));
    },
    fileList,
  };

  const handleSubmit = async (shouldExit = false) => {
    if (!validateForm()) return;
    let projectDoc = projectFormData.project_doc;

    if (modalType === "edit" && (!fileList.length || fileList[0].url)) {
      projectDoc = null;
    }

    const updatedFormData = {
      ...projectFormData,
      created_by: modalType === "create" ? user.UserId : undefined,
      modified_by: modalType === "edit" ? user.UserId : undefined,
    };
    try {
      let result;
      if (modalType === "create") {
        result = await dispatch(createProject(updatedFormData)).unwrap();
      } else if (modalType === "edit") {
        if (selectedRowData) {
          result = await dispatch(
            updateProject({
              projectId: selectedRowData.project_id,
              ...updatedFormData,
            })
          ).unwrap();
        }
      }
      if (result) {
        message.success(result.message);
        await dispatch(fetchAllProjects());
        resetForm();
        resetProjectMemberForm();
        setTableKey((prevKey) => prevKey + 1);
        if (shouldExit) {
          resetForm();
          handleCancel();
        }
      } else {
        alert(result?.payload.error);
      }
    } catch (error) {
      message.error("An error occurred");
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    try {
      const result = await dispatch(deleteProject({ projectId })).unwrap();
      if (result) {
        message.success(result.message);
        await dispatch(fetchAllProjects());
        setTableKey((prevKey) => prevKey + 1);
        setIsDeleteModalVisible(false);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const handleprojectMemberSubmit = async () => {
    if (!validateProjectMemberForm()) return;

    const projectMemberData = {
      project_id: projects[0]?.projectId || selectedRowData.project_id,
      employee_ids: projectMemberFormData.employee_ids,
      team_lead_employee_id: projectMemberFormData.team_lead_employee_id,
      project_manager_employee_id: projectMemberFormData.project_manager_employee_id,
      projectmembers_status: projectMemberFormData.projectmembers_status,
      created_by: user?.UserId,
      modified_by: user?.UserId,
    };

    try {
      if (selectedRowData && selectedRowData.project_member_id != null) {
        await dispatch(updateProjectMember({
          projectMemberId: selectedRowData.project_member_id,
          ...projectMemberData,
        }));
        message.success("Project member updated successfully");
      } else {
        await dispatch(createProjectMember(projectMemberData));
        message.success("Project member added successfully");
      }

      resetProjectMemberForm();
      await dispatch(fetchAllProjects());
      handleCancel();
    } catch (error) {
      message.error("Failed to add or update project member");
      console.error("Error:", error);
    }
  };


  const data = projectsData ? projectsData : [];

  const exportProjectData = data.map((item: any) => ({
    // project_id: item.project_id,
    project_name: item.project_name || "N/A",
    end_date: item.end_date ? new Date(item.end_date).toLocaleDateString("en-IN") : "N/A",
    start_date: item.start_date ? new Date(item.start_date).toLocaleDateString("en-IN") : "N/A",
    // client_id: item.client_id || "N/A",
    price_type: item.price_type || "N/A",
    project_value: item.project_value || "N/A",
    priority: item.priority || "N/A",
    created_at: item.created_at ? new Date(item.created_at).toLocaleDateString("en-IN") : "N/A",
    status: item.status || "N/A",
    project_progress: item.project_progress || "0%",
    projectmembers_status: item.projectmembers_status || "N/A",
    team_lead_name: item.team_lead_name || "N/A",
    project_manager_name: item.project_manager_name || "N/A",
  }));

  const filteredData = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { status: selectedStatus }
        : {};
    return filterData(
      data || [],
      filters,
      "created_at",
      "created_at",
      selectedSort
    );
  }, [projectsData, selectedStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort, projectsData]);

  //Permissions
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];


  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const config = useAppConfig();
  if (!config) return null;

  const columns = [
    {
      title: "Project Code",
      dataIndex: "projectCode",
      render: (text: number, record: any) => (
        // <Link to={all_routes.projectdetails}>{record.projectCode}</Link>
        <div>{record.projectCode}</div>
      ),
      sorter: (a: any, b: any) => a.projectCode - b.projectCode,
    },
    {
      title: "Project Name",
      dataIndex: "project_name",
      render: (text: string, record: any) => (
        // <h6 className="fw-medium"><Link to={all_routes.projectdetails}>{record.project_name}</Link></h6>
        <h6 className="fw-medium">
          <div>{record.project_name}</div>
        </h6>
      ),
      sorter: (a: any, b: any) => a.project_name.length - b.project_name.length,
    },
    {
      title: "Project Leader",
      dataIndex: "team_lead_name",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <div className="ms-2">
            <h6 className="fw-normal">
              <Link to="#">
                {text && text.trim() !== "" ? text : "Not Available"}
              </Link>
            </h6>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const teamLeadNameA = a.team_lead_name || "";
        const teamLeadNameB = b.team_lead_name || "";
        return teamLeadNameA.length - teamLeadNameB.length;
      },
    },
    {
      title: "Deadline",
      dataIndex: "end_date",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.end_date.length - b.end_date.length,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      render: (text: string) => (
        <div className="d-inline-flex align-items-center">
          <span
            className={`rounded-circle d-flex justify-content-center align-items-center me-2 ${text === "High"
              ? "bg-transparent-danger"
              : text === "Low"
                ? "bg-transparent-success"
                : "bg-transparent-warning"
              }`}
          >
            <i
              className={`ti ti-point-filled ${text === "High"
                ? "text-danger"
                : text === "Low"
                  ? "text-success"
                  : "text-warning"
                }`}
            />
          </span>
          {text}
        </div>
      ),
      sorter: (a: any, b: any) => a.priority.length - b.priority.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: any) => (
        <span
          className={`badge  ${text === "active" ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => {
        const isEditDisabled = !canEdit;
        const isDeleteDisabled = !canDelete || record.status === "inactive";

        return (
          <div className="action-icon d-inline-flex">
            {/* Edit Button */}
            <button
              className="me-2"
              onClick={() => {
                if (!isEditDisabled) {
                  showModal("edit");
                  handleEditProject(record);
                }
              }}
              disabled={isEditDisabled}
              style={{
                cursor: isEditDisabled ? "not-allowed" : "pointer",
                opacity: isEditDisabled ? 0.5 : 1,
              }}
              title={isEditDisabled ? "No permission to edit" : "Edit"}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
            >
              <i className="ti ti-edit" />
            </button>

            {/* Delete Button */}
            <button
              className="me-2"
              onClick={() => {
                if (!isDeleteDisabled) {
                  setSelectedRowData(record);
                  setIsDeleteModalVisible(true);
                }
              }}
              disabled={isDeleteDisabled}
              style={{
                cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                opacity: isDeleteDisabled ? 0.5 : 1,
              }}
              title={isDeleteDisabled ? "No permission to delete" : "Delete"}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
            >
              <i className="ti ti-trash" />
            </button>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            {/* Breadcrumb */}
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">Projects</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Projects
                    </li>
                  </ol>
                </nav>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                {/* <div className="me-2 mb-2">
                  <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
                    <Link
                      to={all_routes.projectlist}
                      className="btn btn-icon btn-sm active bg-primary text-white me-1"
                    >
                      <i className="ti ti-list-tree" />
                    </Link>
                    <Link to={all_routes.project} className="btn btn-icon btn-sm">
                      <i className="ti ti-layout-grid" />
                    </Link>
                  </div>
                </div> */}
                {canExport && (
                  <div className="me-2 mb-2">
                    <div className="dropdown">
                      <Link
                        to="#"
                        className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-file-export me-1" />
                        Export
                      </Link>
                      <ul className="dropdown-menu  dropdown-menu-end p-3">
                        <li>
                          <button
                            onClick={() => exportToCSV(exportProjectData, "project_data")}
                            className="dropdown-item rounded-1"
                          >
                            <i className="ti ti-file-type-pdf me-1" />
                            Export as CSV
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => exportToExcel(exportProjectData, "project_data")}
                            className="dropdown-item rounded-1"
                          >
                            <i className="ti ti-file-type-xls me-1" />
                            Export as Excel{" "}
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => exportToPDF(exportProjectData, "project_data")}
                            className="dropdown-item rounded-1"
                          >
                            <i className="ti ti-file-type-xls me-1" />
                            Export as PDF{" "}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {canAdd && (
                  <div className="mb-2">
                    <button
                      onClick={() => showModal("create")}
                      className="btn btn-primary d-flex align-items-center"
                    >
                      <i className="ti ti-circle-plus me-2" />
                      Add Project
                    </button>
                  </div>
                )}
                <div className="ms-2 head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
            {/* /Breadcrumb */}
            {/* Project list */}
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h5>Project List</h5>
                <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                  <div className="dropdown me-3">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      {selectedStatus
                        ? selectedStatus.charAt(0).toUpperCase() +
                        selectedStatus.slice(1)
                        : "Select Status"}
                    </Link>
                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedStatus("All")}
                          className="dropdown-item rounded-1"
                        >
                          All
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedStatus("active")}
                          className="dropdown-item rounded-1"
                        >
                          Active
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedStatus("inactive")}
                          className="dropdown-item rounded-1"
                        >
                          Inactive
                        </Link>
                      </li>
                    </ul>
                  </div>
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      {selectedSort
                        ? `Sort By : ${selectedSort}`
                        : "Sort By : Last 7 Days"}
                    </Link>
                    <ul className="dropdown-menu dropdown-menu-end p-3">
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedSort("Recently Added")}
                          className="dropdown-item rounded-1"
                        >
                          Recently Added
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedSort("Ascending")}
                          className="dropdown-item rounded-1"
                        >
                          Ascending
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedSort("Descending")}
                          className="dropdown-item rounded-1"
                        >
                          Descending
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedSort("Last Month")}
                          className="dropdown-item rounded-1"
                        >
                          Last Month
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          onClick={() => setSelectedSort("Last 7 Days")}
                          className="dropdown-item rounded-1"
                        >
                          Last 7 Days
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <Table
                  key={tableKey}
                  dataSource={filteredData}
                  columns={columns}
                  Selection={true}
                />
              </div>
            </div>
            {/* / Project list  */}
          </div>
          <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <p className="mb-0">2024 - 2025© hrmtree.</p>
            <p>
              Developed By{" "}
              <Link to="#" className="text-primary">
                Leadchain Systems
              </Link>
            </p>
          </div>
        </div>
        {/* /Page Wrapper */}
      </>

      {/* Add Project */}
      <Drawer
        title={modalType === "edit" ? "Edit Project" : "Add Project"}
        placement="right"
        width={700}
        onClose={handleCancel}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <div className="add-info-fieldset ">
          <div className="contact-grids-tab p-3 pb-0">
            <ul className="nav nav-underline" id="myTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  id="basic-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#basic-info"
                  type="button"
                  role="tab"
                  aria-selected="true"
                >
                  Basic Information
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link"
                  id="member-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#member"
                  type="button"
                  role="tab"
                  aria-selected="false"
                >
                  Members
                </button>
              </li>
            </ul>
          </div>
          <div className="tab-content" id="myTabContent">
            <div
              className="tab-pane fade show active"
              id="basic-info"
              role="tabpanel"
              aria-labelledby="basic-tab"
              tabIndex={0}
            >
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Project Name</label>
                        <span className="text-danger ms-2">*</span>
                        <input
                          type="text"
                          className="form-control"
                          value={projectFormData.project_name}
                          onChange={(e) => handleInputChange(e, "project_name")}
                        />
                        {errors.project_name && (
                          <small className="text-danger">
                            {errors.project_name}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Client</label>{" "}
                        <span className="text-danger ms-2">*</span>
                        <select
                          className="form-control"
                          value={projectFormData.client_id}
                          onChange={(e) => handleInputChange(e, "client_id")}
                        >
                          <option value="">Select Client</option>
                          {clientData
                            .sort((a: any, b: any) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                            .map((client: any) => (
                              <option
                                key={client.client_id}
                                value={client.client_id}
                              >
                                {client.first_name} {client.last_name}
                              </option>
                            ))}
                        </select>
                        {errors.client_id && (
                          <small className="text-danger">
                            {errors.client_id}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Total hours</label>
                        <span className="text-danger ms-2">*</span>
                        <input
                          type="text"
                          className="form-control"
                          value={projectFormData.total_hours}
                          onChange={(e) => handleInputChange(e, "total_hours")}
                        />
                        {errors.total_hours && (
                          <small className="text-danger">
                            {errors.total_hours}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="row">
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Start Date</label>
                            <div className="input-icon-end position-relative">
                              <DatePicker
                                className="form-control datetimepicker"
                                format="DD-MM-YYYY"
                                getPopupContainer={getModalContainer}
                                value={projectFormData.start_date}
                                onChange={(date) =>
                                  setProjectFormData({
                                    ...projectFormData,
                                    start_date: date,
                                  })
                                }
                                placeholder="DD-MM-YYYY"
                              />
                              <span className="input-icon-addon">
                                <i className="ti ti-calendar text-gray-7" />
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">End Date</label>
                            <div className="input-icon-end position-relative">
                              <DatePicker
                                className="form-control datetimepicker"
                                format="DD-MM-YYYY"
                                getPopupContainer={getModalContainer}
                                value={projectFormData.end_date}
                                onChange={(date) =>
                                  setProjectFormData({
                                    ...projectFormData,
                                    end_date: date,
                                  })
                                }
                                placeholder="DD-MM-YYYY"
                              />
                              <span className="input-icon-addon">
                                <i className="ti ti-calendar text-gray-7" />
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">
                              Progress Status
                            </label>
                            <span className="text-danger ms-2">*</span>
                            <select
                              className="form-control"
                              value={projectFormData.project_progress}
                              onChange={(e) =>
                                handleInputChange(e, "project_progress")
                              }
                            >
                              {projectProgress.map((p: any) => (
                                <option key={p.value} value={p.value}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                            {errors.project_progress && (
                              <small className="text-danger">
                                {errors.project_progress}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Priority</label>
                            <span className="text-danger ms-2">*</span>
                            <select
                              className="form-control"
                              value={projectFormData.priority}
                              onChange={(e) => handleInputChange(e, "priority")}
                            >
                              {priority.map((p: any) => (
                                <option key={p.value} value={p.value}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                            {errors.priority && (
                              <small className="text-danger">
                                {errors.priority}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Project Value</label>{" "}
                            <span className="text-danger ms-2">*</span>
                            <input
                              type="text"
                              className="form-control"
                              value={projectFormData.project_value}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow only numeric input
                                if (/^\d*$/.test(value)) {
                                  handleInputChange(e, "project_value");
                                }
                              }}
                              title="Only numbers are allowed"
                            />
                            {errors.project_value && (
                              <small className="text-danger">
                                {errors.project_value}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Currency Type</label>
                            <span className="text-danger ms-2">*</span>
                            <select
                              className="form-control"
                              value={projectFormData.price_type}
                              onChange={(e) => handleInputChange(e, "price_type")}
                            >
                              <option value="">Select Currency</option>
                              <option value="INR">INR - Indian Rupee</option>
                              <option value="USD">USD - US Dollar</option>
                              <option value="EUR">EUR - Euro</option>
                              <option value="GBP">GBP - British Pound</option>
                              <option value="JPY">JPY - Japanese Yen</option>
                              <option value="AUD">AUD - Australian Dollar</option>
                              <option value="CAD">CAD - Canadian Dollar</option>
                              <option value="CNY">CNY - Chinese Yuan</option>
                            </select>
                            {errors.price_type && (
                              <small className="text-danger">{errors.price_type}</small>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Status</label>{" "}
                        <span className="text-danger ms-2">*</span>
                        <select
                          className="form-control"
                          name="status"
                          value={projectFormData.status}
                          onChange={(e) => handleInputChange(e, "status")}
                        >
                          {projectStatus?.map((status: any) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                        {errors.status && (
                          <small className="text-danger">{errors.status}</small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="input-block mb-0">
                        <Upload {...props} maxCount={1}>
                          <Button icon={<UploadOutlined />}>Click to Upload</Button>
                        </Upload>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer mt-3">
                  <div className="d-flex align-items-center justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-light border me-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary me-2"
                      type="button"
                      onClick={() => handleSubmit(false)} // Save but keep modal open
                      disabled={loading}
                    >
                      {loading && <span className="spinner-border spinner-border-sm me-2" />}
                      {modalType === 'edit' ? 'Update Project' : 'Add Project'}
                    </button>
                    <button
                      className="btn btn-success"
                      type="button"
                      onClick={() => handleSubmit(true)} // Save & close modal
                      disabled={loading}
                    >
                      {loading && <span className="spinner-border spinner-border-sm me-2" />}
                      {modalType === 'edit' ? 'Update & Exit' : 'Add & Exit'}
                    </button>
                  </div>
                </div>


              </form>
            </div>
            <div
              className="tab-pane fade"
              id="member"
              role="tabpanel"
              aria-labelledby="member-tab"
              tabIndex={0}
            >
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label me-2">Team Members</label>{" "}
                        <span className="text-danger ms-2">*</span>
                        <Select
                          isMulti
                          options={[...employeeData].sort((a: any, b: any) => a.Name.localeCompare(b.Name))
                            .map((emp: any) => ({
                              value: emp.EmployeeID,
                              label: emp.Name,
                            }))}
                          value={employeeData
                            .filter((emp: any) =>
                              projectMemberFormData.employee_ids.includes(
                                emp.EmployeeID
                              )
                            )
                            .map((emp: any) => ({
                              value: emp.EmployeeID,
                              label: emp.Name,
                            }))}
                          onChange={(selectedEmployees: EmployeeOption[]) =>
                            handleProjectMemberMultiChange(
                              "employee_ids",
                              selectedEmployees
                            )
                          }
                        />
                        {membersErrors.employee_ids && (
                          <small className="text-danger">
                            {membersErrors.employee_ids}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label me-2">Team Leader</label>
                        <span className="text-danger ms-2">*</span>
                        <select
                          className="form-control"
                          value={projectMemberFormData.team_lead_employee_id}
                          onChange={(e) =>
                            handleProjectMemberChange(
                              "team_lead_employee_id",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select Team Leader</option>
                          {[...employeeData]
                            .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
                            .map((emp: any) => (
                              <option key={emp.EmployeeID} value={emp.EmployeeID}>
                                {emp.Name}
                              </option>
                            ))}
                        </select>
                        {membersErrors.team_lead_employee_id && (
                          <small className="text-danger">
                            {membersErrors.team_lead_employee_id}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label me-2">
                          Project Manager
                        </label>
                        <span className="text-danger ms-2">*</span>
                        <select
                          className="form-control"
                          value={
                            projectMemberFormData.project_manager_employee_id
                          }
                          onChange={(e) =>
                            handleProjectMemberChange(
                              "project_manager_employee_id",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select Project Manager</option>
                          {[...employeeData]
                            .sort((a: any, b: any) => a.Name.localeCompare(b.Name))
                            .map((emp: any) => (
                              <option key={emp.EmployeeID} value={emp.EmployeeID}>
                                {emp.Name}
                              </option>
                            ))}
                        </select>
                        {membersErrors.project_manager_employee_id && (
                          <small className="text-danger">
                            {membersErrors.project_manager_employee_id}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">Status</label>{" "}
                        <span className="text-danger ms-2">*</span>
                        <select
                          className="form-control"
                          value={projectMemberFormData.projectmembers_status}
                          onChange={(e) =>
                            handleProjectMemberChange(
                              "projectmembers_status",
                              e.target.value
                            )
                          }
                        >
                          {statusChoose.map((status: any) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                        {membersErrors.projectmembers_status && (
                          <small className="text-danger">
                            {membersErrors.projectmembers_status}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex align-items-center justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-light border me-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handleprojectMemberSubmit}
                    >
                      {modalType === "edit" ? "Update Member" : "Add Member"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Drawer>
      {/* /Add Project */}
      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.project_id &&
          handleDeleteProject(selectedRowData.project_id)
        }
      />
    </>
  );
};

export default ProjectList;
